/**
 * One colour -> the whole dashboard palette.
 *
 * Tailwind's blue-* and indigo-* classes read `--blue-<shade>` /
 * `--indigo-<shade>` (tailwind.config.ts, defaults in global.css). This module
 * builds those shades from the brand colour so every hard-coded blue button,
 * hover, active menu item and gradient follows it, and writes `--primary`,
 * `--accent` and `--gradient-primary` alongside.
 *
 * The brand colour becomes shade 600 (the usual button colour). Lighter shades
 * step towards white and darker ones towards black in the same proportions as
 * Tailwind's own blue, so `hover:bg-blue-700`, `bg-blue-50` tints and
 * `text-blue-100` on a blue header keep working for any colour.
 *
 * ThemeContext is the only caller. Pure apart from `applyBrandPalette`.
 */

export const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

/** Tailwind blue's saturation per shade, relative to shade 600 (83.2%). */
const SAT_RATIO: Record<number, number> = {
  50: 1.2, 100: 1.14, 200: 1.16, 300: 1.16, 400: 1.13, 500: 1.1,
  600: 1, 700: 0.92, 800: 0.85, 900: 0.77, 950: 0.69,
};
/** Lighter shades: how far from the brand colour towards white. */
const TINT: Record<number, number> = { 50: 0.935, 100: 0.845, 200: 0.73, 300: 0.54, 400: 0.31, 500: 0.14 };
/** Darker shades: lightness as a share of the brand colour's. */
const SHADE: Record<number, number> = { 700: 0.9, 800: 0.755, 900: 0.617, 950: 0.394 };

type Hsl = { h: number; s: number; l: number };

export const parseHsl = (token: string | null | undefined): Hsl | null => {
  const m = /^\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*$/.exec(token || "");
  return m ? { h: Number(m[1]), s: Number(m[2]), l: Number(m[3]) } : null;
};

const fmt = ({ h, s, l }: Hsl) => {
  const r = (x: number) => Math.round(x * 10) / 10;
  return `${r(((h % 360) + 360) % 360)} ${r(Math.min(100, Math.max(0, s)))}% ${r(Math.min(100, Math.max(0, l)))}%`;
};

/** {50: "H S% L%", ... 950: ...} with `base` as shade 600. */
export function shadeScale(base: Hsl, hueShift = 0): Record<number, string> {
  const h = base.h + hueShift;
  const out: Record<number, string> = {};
  for (const shade of SHADES) {
    const s = base.s * SAT_RATIO[shade];
    let l = base.l;
    if (shade in TINT) l = base.l + (100 - base.l) * TINT[shade];
    else if (shade in SHADE) l = base.l * SHADE[shade];
    out[shade] = fmt({ h, s, l });
  }
  return out;
}

/** Variables this module may set on :root (cleared again for the default look). */
const PALETTE_VARS = [
  ...SHADES.map((s) => `--blue-${s}`),
  ...SHADES.map((s) => `--indigo-${s}`),
];

/**
 * Write the palette for `primaryHsl` on `root`.
 *
 * `recolourPalette` false keeps Tailwind's own blue/indigo (the platform's
 * default look, so an untouched install looks exactly as before).
 */
export function applyBrandPalette(
  root: HTMLElement,
  primaryHsl: string,
  accentHsl: string,
  recolourPalette: boolean,
): void {
  root.style.setProperty("--primary", primaryHsl);
  root.style.setProperty("--accent", accentHsl);
  root.style.setProperty(
    "--gradient-primary",
    `linear-gradient(135deg, hsl(${primaryHsl}) 0%, hsl(${accentHsl}) 100%)`,
  );

  const base = recolourPalette ? parseHsl(primaryHsl) : null;
  if (!base) {
    PALETTE_VARS.forEach((v) => root.style.removeProperty(v));
    return;
  }
  const blue = shadeScale(base);
  // Indigo sits a little further round the colour wheel, so the app's
  // blue-to-indigo gradients still read as a gradient.
  const indigo = shadeScale(base, 18);
  for (const shade of SHADES) {
    root.style.setProperty(`--blue-${shade}`, blue[shade]);
    root.style.setProperty(`--indigo-${shade}`, indigo[shade]);
  }
}
